/** `/sessions/join`: "Rejoindre" — every open session, with its own progress. */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { joinSessionRequest, listSessions } from '../../../api/sessions';
import { Button, EmptyState, Skeleton, useToast } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { SessionCard } from '../components/SessionCard';
import { sessionErrorMessage } from '../errors';

import type { SessionWithExtras } from '../../../api/types';

/**
 * Render the "Rejoindre une session" screen.
 *
 * @returns The page element.
 */
export function JoinSessionPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [sessions, setSessions] = useState<SessionWithExtras[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [joiningId, setJoiningId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    listSessions({ state: 'open' })
      .then((page) => {
        if (!cancelled) {
          setSessions(page.results);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setSessions([]);
          showToast(sessionErrorMessage(error), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  async function handleJoin(id: number) {
    setJoiningId(id);
    try {
      await joinSessionRequest(id);
      navigate(`/sessions/${id}`);
    } catch (error) {
      showToast(sessionErrorMessage(error), 'error');
    } finally {
      setJoiningId(null);
    }
  }

  return (
    <div className="pile">
      <div className="bloc">
        <h2>{t('session.join.title')}</h2>
        <p className="muted" style={{ margin: '0 0 var(--space-3)' }}>
          {t('session.join.lead')}
        </p>
        {loading ? (
          <div className="pile" aria-hidden="true">
            <Skeleton height="8rem" />
            <Skeleton height="8rem" />
          </div>
        ) : sessions && sessions.length > 0 ? (
          sessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              detailed
              action={{
                label:
                  joiningId === session.id ? t('session.join.joining') : t('session.join.joinCta'),
                onClick: () => void handleJoin(session.id),
                disabled: joiningId !== null,
              }}
            />
          ))
        ) : (
          <EmptyState title={t('session.join.title')} body={t('session.join.empty')} />
        )}
      </div>
      <div className="bloc">
        <Button variant="ghost" fullWidth onClick={() => navigate('/')}>
          {t('session.join.back')}
        </Button>
      </div>
    </div>
  );
}
